// src/components/emergencias/ModalVerEmergencia.js
import React, { useState, useEffect } from 'react';
import { 
  X, 
  MapPin, 
  Calendar, 
  AlertCircle, 
  User,
  FileText,
  Image as ImageIcon,
  Download,
  ZoomIn,
  ChevronLeft,
  ChevronRight,
  Clock,
  Wrench,
  CheckCircle2,
  DollarSign
} from 'lucide-react';
import { maquinariaAPI } from '../../services/apiService';

const ModalVerEmergencia = ({ isOpen, onClose, emergencia }) => {
  const [imagenExpandida, setImagenExpandida] = useState(null);
  const [indiceImagenActual, setIndiceImagenActual] = useState(0);
  const [tiposMaquinas, setTiposMaquinas] = useState([]);
  const [loadingMaquinas, setLoadingMaquinas] = useState(true);

  // Cargar tipos de máquinas al abrir el modal
  useEffect(() => {
    if (isOpen) {
      cargarTiposMaquinas();
    }
  }, [isOpen]);

  const cargarTiposMaquinas = async () => {
    try {
      setLoadingMaquinas(true);
      const data = await maquinariaAPI.getTipos();
      setTiposMaquinas(data);
    } catch (error) {
      console.error('Error al cargar tipos de máquinas:', error);
      setTiposMaquinas([]);
    } finally {
      setLoadingMaquinas(false);
    }
  };

  // Función para obtener información de una máquina
  const obtenerInfoMaquina = (idMaquina) => {
    if (!tiposMaquinas || tiposMaquinas.length === 0) {
      return {
        Tipo: 'Cargando...',
        Precio: 0,
        Disposicion: 'N/A'
      };
    }
    
    const maquina = tiposMaquinas.find(m => m.Id === idMaquina);
    
    if (maquina) {
      return {
        Id: maquina.Id,
        Tipo: maquina.Tipo ? maquina.Tipo.trim() : 'Desconocido',
        Precio: maquina.Precio || 0,
        Disposicion: maquina.Disposicion || 'N/A',
        ContratistaFK: maquina.ContratistaFK
      };
    }
    
    return {
      Tipo: 'Desconocido',
      Precio: 0,
      Disposicion: 'N/A'
    };
  };

  if (!isOpen || !emergencia) return null;

  // Función para formatear fechas
  const formatearFecha = (fechaString) => {
    if (!fechaString) return 'N/A';
    
    try {
      // Formato DD-MM-YYYY (ej: "27-07-2025")
      if (fechaString.includes('-')) {
        const partes = fechaString.split('-');
        
        // Si es DD-MM-YYYY
        if (partes.length === 3 && partes[0].length <= 2) {
          const [dia, mes, año] = partes;
          const fecha = new Date(parseInt(año), parseInt(mes) - 1, parseInt(dia));
          
          if (!isNaN(fecha.getTime())) {
            return fecha.toLocaleDateString('es-HN', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            });
          }
        }
        
        // Si es YYYY-MM-DD
        if (partes.length === 3 && partes[0].length === 4) {
          const fecha = new Date(fechaString);
          if (!isNaN(fecha.getTime())) {
            return fecha.toLocaleDateString('es-HN', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            });
          }
        }
      }
      
      // Intentar parsear como fecha ISO
      const fecha = new Date(fechaString);
      if (!isNaN(fecha.getTime())) {
        return fecha.toLocaleDateString('es-HN', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      }
      
      // Si todo falla, retornar la fecha original
      return fechaString;
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return fechaString;
    }
  };

  // Función para formatear moneda
  const formatearMoneda = (valor) => {
    if (!valor && valor !== 0) return 'L. 0.00';
    return `L. ${Number(valor).toLocaleString('es-HN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  // Obtener las imágenes del anexo (separadas por comas)
  const obtenerImagenes = () => {
    const imagenes = [];

    if (emergencia.Anexo && typeof emergencia.Anexo === 'string' && emergencia.Anexo.trim() !== '') {
      const urls = emergencia.Anexo.split(',').map(url => url.trim()).filter(url => url !== '');
      
      urls.forEach((url, index) => {
        imagenes.push({ 
          url: url, 
          nombre: `Anexo ${index + 1}` 
        });
      });
    }

    return imagenes;
  };

  const imagenes = obtenerImagenes();

  // Función para descargar imagen
  const descargarImagen = (url, nombre) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = nombre || 'imagen.jpg';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Función para abrir imagen en modal
  const abrirImagenExpandida = (index) => {
    setIndiceImagenActual(index);
    setImagenExpandida(imagenes[index]);
  };

  // Navegar entre imágenes
  const imagenAnterior = () => {
    const nuevoIndice = (indiceImagenActual - 1 + imagenes.length) % imagenes.length;
    setIndiceImagenActual(nuevoIndice);
    setImagenExpandida(imagenes[nuevoIndice]);
  };

  const imagenSiguiente = () => {
    const nuevoIndice = (indiceImagenActual + 1) % imagenes.length;
    setIndiceImagenActual(nuevoIndice);
    setImagenExpandida(imagenes[nuevoIndice]);
  };

  // Calcular totales de máquinas utilizadas
  const calcularTotalMaquinasUtilizadas = () => {
    if (!emergencia.MáquinasUtilizadas || emergencia.MáquinasUtilizadas.length === 0) {
      return 0;
    }
    return emergencia.MáquinasUtilizadas.reduce((total, maq) => total + (maq.Total || 0), 0);
  };

  return (
    <>
      {/* Modal Principal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-white p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Detalles de Emergencia</h2>
                <p className="text-gray-600 mt-1">ID: {emergencia.Id}</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Contenido con scroll */}
          <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6 space-y-6">
            
            {/* Información Principal */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Ubicación */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Ubicación</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">Municipio:</span>
                    <p className="font-medium text-gray-900">{emergencia.NombreMunicipio || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Zona Cafetalera:</span>
                    <p className="font-medium text-gray-900">{emergencia.ZonaCafetalera || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Tramo:</span>
                    <p className="font-medium text-gray-900">{emergencia.NombreTramo || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Longitud del Tramo:</span>
                    <p className="font-medium text-gray-900">{emergencia.LongitudTramo ? `${emergencia.LongitudTramo} km` : 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Tipo y Estado */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Tipo de Emergencia</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <span className="text-gray-500 text-sm">Tipo:</span>
                    <p className="font-medium text-gray-900 text-lg">{emergencia.NombreTipoEmergencia || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Fenómeno:</span>
                    <p className="font-medium text-gray-900">{emergencia.NombreFenomeno || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">Estado:</span>
                    <div className="mt-1">
                      <EstadoBadge estado={emergencia.Estado} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Fechas */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Fechas</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">Fecha:</span>
                    <p className="font-medium text-gray-900">{formatearFecha(emergencia.Fecha)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Creada:</span>
                    <p className="font-medium text-gray-900">{formatearFecha(emergencia.FechaCreacion)}</p>
                  </div>
                  {emergencia.FechaActualizacion && (
                    <div>
                      <span className="text-gray-500">Actualizada:</span>
                      <p className="font-medium text-gray-900">{formatearFecha(emergencia.FechaActualizacion)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Responsables */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <User className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Responsables</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">Ejecutor:</span>
                    <p className="font-medium text-gray-900">{emergencia.Ejecutor || 'No asignado'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Creado por:</span>
                    <p className="font-medium text-gray-900">{emergencia.NombreCreador || 'N/A'}</p>
                  </div>
                  {emergencia.NombreActualizador && (
                    <div>
                      <span className="text-gray-500">Actualizado por:</span>
                      <p className="font-medium text-gray-900">{emergencia.NombreActualizador}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Inversión */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-gray-600" />
                </div>
                <h3 className="font-semibold text-gray-900">Información Financiera</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <span className="text-gray-500 text-sm">Inversión Proyectada:</span>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {formatearMoneda(emergencia.InversionProyectada)}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <span className="text-gray-500 text-sm">Inversión Total:</span>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {formatearMoneda(emergencia.InversionTotal)}
                  </p>
                </div>
              </div>
            </div>

            {/* MÁQUINAS PREVISTAS */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="bg-gray-50 p-4 rounded-t-xl border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Wrench className="w-5 h-5 text-gray-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg">Máquinas Previstas</h3>
                </div>
              </div>

              <div className="p-4">
                {loadingMaquinas ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto"></div>
                    <p className="text-gray-500 mt-4">Cargando información de máquinas...</p>
                  </div>
                ) : emergencia.MaquinasPrevistas && emergencia.MaquinasPrevistas.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Código
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tipo de Máquina
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Precio/Hora
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Disposición
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {emergencia.MaquinasPrevistas.map((maq, index) => {
                          const info = obtenerInfoMaquina(maq.TipoMaquinaFK);
                          return (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                                  {maq.TipoMaquinaFK}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="text-sm font-medium text-gray-900">{info.Tipo}</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="text-sm font-medium text-gray-900">
                                  {formatearMoneda(info.Precio)}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="text-sm text-gray-700">
                                  {info.Disposicion || 'No especificada'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Wrench className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="font-medium">No hay máquinas previstas</p>
                  </div>
                )}
              </div>
            </div>

            {/* MÁQUINAS UTILIZADAS */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="bg-gray-50 p-4 rounded-t-xl border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-gray-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-lg">Máquinas Utilizadas</h3>
                  </div>
                  {emergencia.MáquinasUtilizadas && emergencia.MáquinasUtilizadas.length > 0 && (
                    <div className="bg-gray-100 px-4 py-2 rounded-lg">
                      <span className="text-gray-900 text-sm font-medium">
                        Total: {formatearMoneda(calcularTotalMaquinasUtilizadas())}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4">
                {loadingMaquinas ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto"></div>
                    <p className="text-gray-500 mt-4">Cargando información de máquinas...</p>
                  </div>
                ) : emergencia.MáquinasUtilizadas && emergencia.MáquinasUtilizadas.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Código
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tipo de Máquina
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cantidad
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Horas
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Precio/Hora
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {emergencia.MáquinasUtilizadas.map((maq, index) => {
                          const info = obtenerInfoMaquina(maq.TipoMaquinaFK);
                          return (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                                  {maq.TipoMaquinaFK}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="text-sm font-medium text-gray-900">{info.Tipo}</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm font-medium text-gray-900">{maq.Cantidad}</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm font-medium text-gray-900">
                                  {maq.TotalHorasTrabajadas} hrs
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right">
                                <span className="text-sm font-medium text-gray-900">
                                  {formatearMoneda(maq.PrecioPorHora)}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right">
                                <span className="text-sm font-bold text-gray-900">
                                  {formatearMoneda(maq.Total)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan="5" className="px-4 py-3 text-right font-bold text-gray-900">
                            Total General:
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-lg font-bold text-gray-900">
                              {formatearMoneda(calcularTotalMaquinasUtilizadas())}
                            </span>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="font-medium">No se han utilizado máquinas aún</p>
                    <p className="text-sm text-gray-400 mt-1">Las máquinas utilizadas aparecerán aquí una vez sean asignadas</p>
                  </div>
                )}
              </div>
            </div>

            {/* Coordenadas */}
            {(emergencia.Latitud || emergencia.Longitud) && (
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <MapPin className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Coordenadas</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Latitud:</span>
                    <p className="font-medium text-gray-900">{emergencia.Latitud || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Longitud:</span>
                    <p className="font-medium text-gray-900">{emergencia.Longitud || 'N/A'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Observaciones */}
            {emergencia.Observaciones && (
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <FileText className="w-5 h-5 text-yellow-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Observaciones</h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{emergencia.Observaciones}</p>
              </div>
            )}

            {/* Imágenes Anexas */}
            {imagenes.length > 0 && (
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-pink-100 rounded-lg">
                    <ImageIcon className="w-5 h-5 text-pink-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Imágenes Anexas</h3>
                  <span className="ml-auto bg-pink-100 text-pink-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                    {imagenes.length} {imagenes.length === 1 ? 'imagen' : 'imágenes'}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {imagenes.map((imagen, index) => (
                    <div 
                      key={index}
                      className="group relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-blue-400"
                    >
                      {/* Imagen */}
                      <div className="aspect-square overflow-hidden bg-gray-100">
                        <img
                          src={imagen.url}
                          alt={imagen.nombre}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f3f4f6" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-family="Arial" font-size="14"%3ESin imagen%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      </div>

                      {/* Overlay con acciones */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300 flex items-center justify-center gap-2">
                        <button
                          onClick={() => abrirImagenExpandida(index)}
                          className="opacity-0 group-hover:opacity-100 transform scale-0 group-hover:scale-100 transition-all duration-300 p-3 bg-white rounded-full hover:bg-blue-50"
                          title="Ver en grande"
                        >
                          <ZoomIn className="w-5 h-5 text-blue-600" />
                        </button>
                        <button
                          onClick={() => descargarImagen(imagen.url, imagen.nombre)}
                          className="opacity-0 group-hover:opacity-100 transform scale-0 group-hover:scale-100 transition-all duration-300 p-3 bg-white rounded-full hover:bg-green-50"
                          title="Descargar"
                        >
                          <Download className="w-5 h-5 text-green-600" />
                        </button>
                      </div>

                      {/* Nombre de la imagen */}
                      <div className="p-2 bg-white border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700 truncate text-center">
                          {imagen.nombre}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mensaje si no hay imágenes */}
            {imagenes.length === 0 && (
              <div className="bg-gray-50 rounded-xl p-6 border-2 border-dashed border-gray-300 text-center">
                <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">No hay imágenes adjuntas</p>
                <p className="text-sm text-gray-500 mt-1">Esta emergencia no tiene anexos de imágenes</p>
              </div>
            )}

          </div>


          <div className="bg-gray-50 p-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {imagenExpandida && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-[60] p-4"
          onClick={() => setImagenExpandida(null)}
        >
          <div className="relative max-w-7xl w-full max-h-full flex items-center justify-center">
            
            {/* Botón Cerrar */}
            <button
              onClick={() => setImagenExpandida(null)}
              className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Botones de Navegación */}
            {imagenes.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    imagenAnterior();
                  }}
                  className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    imagenSiguiente();
                  }}
                  className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </>
            )}

            {/* Imagen Grande */}
            <div 
              className="relative max-w-full max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={imagenExpandida.url}
                alt={imagenExpandida.nombre}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
              
              {/* Info de la imagen */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
                <div className="flex items-center justify-between text-white">
                  <div>
                    <p className="font-semibold text-lg">{imagenExpandida.nombre}</p>
                    {imagenes.length > 1 && (
                      <p className="text-sm text-white/80 mt-1">
                        Imagen {indiceImagenActual + 1} de {imagenes.length}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      descargarImagen(imagenExpandida.url, imagenExpandida.nombre);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  >
                    <Download className="w-5 h-5" />
                    <span className="font-medium">Descargar</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Hint de navegación */}
          {imagenes.length > 1 && (
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <p className="text-white text-sm">
                Usa las flechas ← → para navegar entre imágenes
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};


const EstadoBadge = ({ estado }) => {
  const statusConfig = {
    'Por Atender': {
      bg: 'bg-orange-100',
      text: 'text-orange-700',
      border: 'border-orange-200',
      icon: Clock
    },
    'En Atención': {
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      border: 'border-blue-200',
      icon: Clock
    },
    'Atendido': {
      bg: 'bg-green-100',
      text: 'text-green-700',
      border: 'border-green-200',
      icon: CheckCircle2
    }
  };

  const config = statusConfig[estado] || statusConfig['Por Atender'];
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold border ${config.bg} ${config.text} ${config.border}`}>
      <Icon className="w-4 h-4" />
      {estado}
    </span>
  );
};

export default ModalVerEmergencia;